import { useState, useRef, useEffect, useCallback } from "react";

interface NominatimResult {
  place_id: number;
  display_name: string;
  lat: string;
  lon: string;
  address?: {
    house_number?: string;
    road?: string;
    city?: string;
    town?: string;
    village?: string;
    state?: string;
    postcode?: string;
    county?: string;
  };
}

export interface SelectedAddress {
  address: string;
  city: string;
  state: string;
  zip: string;
  latitude: number;
  longitude: number;
  displayName: string;
}

interface AddressAutocompleteProps {
  initialValue?: string;
  onSelect: (address: SelectedAddress) => void;
}

export function AddressAutocomplete({ initialValue = "", onSelect }: AddressAutocompleteProps) {
  const [query, setQuery] = useState(initialValue);
  const [results, setResults] = useState<NominatimResult[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const containerRef = useRef<HTMLDivElement>(null);

  // Update input when initialValue changes (e.g. profile load)
  useEffect(() => {
    setQuery(initialValue);
  }, [initialValue]);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const search = useCallback(async (q: string) => {
    if (q.length < 3) {
      setResults([]);
      setOpen(false);
      return;
    }

    setLoading(true);
    try {
      const url = `https://nominatim.openstreetmap.org/search?${new URLSearchParams({
        q,
        format: "json",
        limit: "5",
        addressdetails: "1",
        countrycodes: "us",
      })}`;

      const res = await fetch(url, {
        headers: { "User-Agent": "Abejon/1.0" },
      });

      if (!res.ok) return;
      const data = (await res.json()) as NominatimResult[];
      setResults(data);
      setOpen(data.length > 0);
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  }, []);

  function handleChange(value: string) {
    setQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(value), 400);
  }

  function handleSelect(result: NominatimResult) {
    const addr = result.address;
    const street = [addr?.house_number, addr?.road].filter(Boolean).join(" ");
    const city = addr?.city || addr?.town || addr?.village || "";
    const state = addr?.state || "";
    const zip = addr?.postcode || "";

    const selected: SelectedAddress = {
      address: street,
      city,
      state,
      zip,
      latitude: parseFloat(result.lat),
      longitude: parseFloat(result.lon),
      displayName: result.display_name,
    };

    setQuery(result.display_name);
    setOpen(false);
    setResults([]);
    onSelect(selected);
  }

  return (
    <div ref={containerRef} className="relative">
      <label className="mb-1 block text-sm font-medium text-gray-700">
        Address
      </label>
      <input
        type="text"
        value={query}
        onChange={(e) => handleChange(e.target.value)}
        onFocus={() => results.length > 0 && setOpen(true)}
        placeholder="Start typing your street address..."
        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900"
      />
      {loading && (
        <div className="absolute right-3 top-9">
          <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600" />
        </div>
      )}

      {open && results.length > 0 && (
        <ul className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-lg border border-gray-200 bg-white shadow-lg">
          {results.map((r) => (
            <li key={r.place_id}>
              <button
                type="button"
                onClick={() => handleSelect(r)}
                className="w-full px-3 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50"
              >
                {r.display_name}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
